import React, {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

import { ViewerRef } from '@r2u/viewer/dist/types'

import { AuthContext } from '@console/common/contexts/AuthContext'

import {
  createViewerOptions,
  Customer,
  updateViewerOptions,
  UserRole,
  ViewerOptions
} from '../../common/services/api'
import setDefaultSlotsModels from '../utils/setDefaultSlotsModels'
import viewerLoaded from '../utils/viewerLoaded'
import { ProductContext } from './ProductContext'

interface Props {
  children: ReactNode
}

type SlotsModel = ReturnType<ViewerRef['getAllSlotsModels']>
type SlotsMaterial = ReturnType<ViewerRef['getAllSlotsMaterials']>

interface ConfigurationContextData {
  viewerOptions: ViewerOptions
  setViewerOptions: (viewerOptions: ViewerOptions) => void
  setViewerOptionsPartial: (ViewerOptions: Partial<ViewerOptions>) => void
  updateViewer: (data: UpdateViewerProps) => void
  saveViewerOptions: (viewerOptions: ViewerOptions) => Promise<void>
  propsViewerOptions: { bloom: boolean }
  viewerRef: RefObject<ViewerRef>
  loading: boolean
  slotsModels: SlotsModel
  setSlotsModels: (slotsModels: SlotsModel) => void
  slotsMaterials: SlotsMaterial
  setSlotsMaterials: (slotsMateirials: SlotsMaterial) => void
}

interface UpdateViewerProps extends ViewerOptions {
  radius?: number
  dragX?: number
  dragY?: number
  dragZ?: number
}

const isExclusive = (a: unknown, b: unknown): boolean =>
  (a !== undefined && b === undefined) || (a === undefined && b !== undefined)

export const ConfigurationContext: React.Context<ConfigurationContextData> =
  createContext<ConfigurationContextData>({} as ConfigurationContextData)

export const ConfigurationProvider: React.FC<Props> = ({ children }: Props) => {
  const [loading, setLoading] = useState(true)
  const [viewerOptions, setViewerOptions] = useState<ViewerOptions>({} as ViewerOptions)
  const [propsViewerOptions, setPropsViewerOptions] = useState<{ bloom: boolean }>({ bloom: false })
  const [customer, setCustomer] = useState<Customer | null>()
  const viewerRef = useRef<ViewerRef>(null)

  const [slotsModels, setSlotsModels] = useState<SlotsModel>(null)
  const [slotsMaterials, setSlotsMaterials] = useState<SlotsMaterial>(null)

  useEffect(() => {
    viewerLoaded(viewerRef).then(() => {
      if (!viewerRef.current) return

      setSlotsModels(viewerRef.current.getAllSlotsModels())
      setSlotsMaterials(viewerRef.current.getAllSlotsMaterials())
    })
  }, [])

  /**
   * ! Disclaimer
   * @function setDefaultSlotsModels sets an initial configuration for the customizable
   * model and is only meant to be used while the native default glb configuration is not
   * yet implemented. When such a feature is implemented on the viewer, the following
   * segment of the code should be deleted.
   */

  // TODO Delete code segment start
  if (viewerRef.current) {
    setDefaultSlotsModels({
      slotsModels,
      setSlotsModels,
      setSlotsMaterials,
      viewerRef: viewerRef.current
    })
  }
  // TODO Delete code segment end

  const { product } = useContext(ProductContext)
  const { session, sessionCustomer, assumedCustomer } = useContext(AuthContext)

  useEffect(() => {
    setCustomer(null)

    const customerOwner = product?.Customers?.find((c) => product.owner === c.id)

    const pCustomer = product?.Customers?.find(
      (c) => c.id === (sessionCustomer?.id || session?.customer.id)
    )

    setCustomer(
      session
        ? session.role === UserRole.CUSTOMER
          ? pCustomer
          : (session.role === UserRole.ADMIN || session.role === UserRole.R2USER) && assumedCustomer
          ? product?.Customers?.find((c) => c.id === assumedCustomer.id) || null
          : product?.Customers?.find((c) => c.id === product.owner)
        : null
    )

    if (customerOwner?.ViewerOption) {
      setViewerOptions(customerOwner.ViewerOption)
      setPropsViewerOptions({ bloom: !!customerOwner.ViewerOption.bloom })
    }
    if (product?.ViewerOption) {
      setViewerOptions(product.ViewerOption)
      setPropsViewerOptions({ bloom: !!product.ViewerOption.bloom })
    }
    if (customer?.CustomerProduct?.ViewerOption) {
      setViewerOptions(customer.CustomerProduct.ViewerOption)
      setPropsViewerOptions({ bloom: !!customer.CustomerProduct.ViewerOption.bloom })
    }

    setLoading(false)
  }, [product, sessionCustomer, session, assumedCustomer])

  const saveViewerOptions = async (data: ViewerOptions) => {
    let newViewerOptions: { status: number; data: ViewerOptions } | null = null

    const payload = {
      ...viewerOptions,
      ...data
    }

    setViewerOptions(payload)

    if (!product || !customer) return

    if (product?.owner === customer?.id) {
      newViewerOptions = await updateViewerOptions({ data: payload, productId: product.id })
      if (newViewerOptions && newViewerOptions.status === 500) return
      if (!newViewerOptions) {
        newViewerOptions = await createViewerOptions(payload, product.id)
      }
    }

    newViewerOptions = await updateViewerOptions({
      data: payload,
      productId: product.id,
      customerId: customer.id
    })
    if (newViewerOptions && newViewerOptions.status === 500) return

    if (!newViewerOptions) {
      newViewerOptions = await createViewerOptions(payload, product.id, customer.id)
    }
  }

  const updateViewer = (data: UpdateViewerProps) => {
    if (!viewerRef) return

    const {
      exposure,
      hdri,
      sceneBackground,
      initialAnimation,
      activeFog,
      fogColor,
      fogNear,
      fogFar,
      activeShadow,
      shadowOpacity,
      shadowOffsetX,
      shadowOffsetY,
      shadowOffsetZ,
      shadowRadius,
      cameraOrbitSensitivity,
      cameraNear,
      cameraFar,
      radius,
      maxZoom,
      minZoom,
      initialZoom,
      maxAzimuthalAngleRotation,
      minAzimuthalAngleRotation,
      maxPolarAngleRotation,
      minPolarAngleRotation,
      maxDragX,
      minDragX,
      maxDragY,
      minDragY,
      maxDragZ,
      minDragZ,
      dragX,
      dragY,
      dragZ,
      initialBloom,
      bloomIntensity,
      bloomRadius,
      bloomThreshold
    } = data

    viewerRef.current?.updateEnvironment({ exposure, hdri, sceneBackground, initialAnimation })
    viewerRef.current?.updateFog({
      enable: activeFog,
      color: fogColor,
      near: fogNear,
      far: fogFar
    })
    viewerRef.current?.updateShadow({
      activeShadow,
      opacity: shadowOpacity,
      XOffset: shadowOffsetX,
      YOffset: shadowOffsetY,
      ZOffset: shadowOffsetZ,
      radius: shadowRadius
    })
    viewerRef.current?.setCamera({
      orbitSensitivity: cameraOrbitSensitivity,
      near: cameraNear,
      far: cameraFar,
      maxRadius: minZoom,
      minRadius: maxZoom,
      initialRadius: initialZoom,
      radius: initialZoom !== undefined ? initialZoom : radius,
      maxAzimuthalAngle: maxAzimuthalAngleRotation,
      minAzimuthalAngle: minAzimuthalAngleRotation,
      maxPolarAngle: maxPolarAngleRotation,
      minPolarAngle: minPolarAngleRotation,
      maxDragX,
      minDragX,
      maxDragY,
      minDragY,
      maxDragZ,
      minDragZ,
      dragX,
      dragY,
      dragZ
    })
    if (initialBloom !== undefined) viewerRef.current?.updateBloom(initialBloom)
    viewerRef.current?.updateBloomProps({
      intensity: bloomIntensity,
      radius: bloomRadius,
      threshold: bloomThreshold
    })

    if (isExclusive(minZoom, maxZoom)) {
      if (minZoom !== undefined) viewerRef.current?.setCamera({ radius: minZoom })
      else viewerRef.current?.setCamera({ radius: maxZoom })
    }

    if (isExclusive(minAzimuthalAngleRotation, maxAzimuthalAngleRotation)) {
      if (minAzimuthalAngleRotation !== undefined) {
        viewerRef.current?.setCamera({ azimuthalAngle: minAzimuthalAngleRotation })
      } else {
        viewerRef.current?.setCamera({ azimuthalAngle: maxAzimuthalAngleRotation })
      }
    }

    if (isExclusive(minPolarAngleRotation, maxPolarAngleRotation)) {
      if (minPolarAngleRotation !== undefined) {
        viewerRef.current?.setCamera({ polarAngle: minPolarAngleRotation })
      } else {
        viewerRef.current?.setCamera({ polarAngle: maxPolarAngleRotation })
      }
    }

    if (isExclusive(minDragX, maxDragX)) {
      if (minDragX !== undefined) {
        viewerRef.current?.setCamera({ dragX: minDragX })
      } else {
        viewerRef.current?.setCamera({ dragX: maxDragX })
      }
    }

    if (isExclusive(minDragY, maxDragY)) {
      if (minDragY !== undefined) {
        viewerRef.current?.setCamera({ dragX: minDragY })
      } else {
        viewerRef.current?.setCamera({ dragX: maxDragY })
      }
    }

    if (isExclusive(minDragZ, maxDragZ)) {
      if (minDragZ !== undefined) {
        viewerRef.current?.setCamera({ dragX: minDragZ })
      } else {
        viewerRef.current?.setCamera({ dragX: maxDragZ })
      }
    }
  }

  return (
    <ConfigurationContext.Provider
      value={{
        viewerOptions,
        setViewerOptions,
        setViewerOptionsPartial: (data: Partial<ViewerOptions>) => {
          updateViewer(data)
          setViewerOptions({ ...viewerOptions, ...data })
        },
        propsViewerOptions,
        updateViewer,
        saveViewerOptions,
        viewerRef,
        loading,
        slotsModels,
        setSlotsModels,
        slotsMaterials,
        setSlotsMaterials
      }}
    >
      {children}
    </ConfigurationContext.Provider>
  )
}
